import { useState, useCallback } from 'react';
import { validateFiles, formatFileSize } from '../utils/uploadValidator';
import projectService from '../services/projectService';
import { notify } from '../components/CustomToast';

/**
 * Hook for handling file uploads with immediate temp upload on file selection
 * Uses deduplication on backend (hash-based via SHA256)
 *
 * Architecture:
 * 1. User selects files → immediately uploaded to /temp-upload (onChange)
 * 2. Each file tracked with status: pending, uploading, uploaded, error
 * 3. Backend DeduplicationService checks if hash exists, returns existing ID or new one
 * 4. Frontend returns upload IDs to caller
 * 5. Task create/update sends only attachment_ids, not files
 * 6. Backend links IDs to task within transaction
 */
export const useFileUpload = () => {
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const uploadFiles = useCallback(async (files) => {
        if (!files || files.length === 0) return [];

        // Validate all files before uploading
        const validation = validateFiles(files);
        if (!validation.valid) {
            notify('error', `File validation errors:\n${validation.errors.join('\n')}`);
            return [];
        }

        const fileArray = Array.from(files);
        const newFiles = fileArray.map((file) => ({
            id: Math.random().toString(36).substr(2, 9), // Temp UI ID
            file,
            name: file.name,
            size: file.size,
            uploadId: null, // Will be set after successful upload
            status: 'pending', // pending, uploading, uploaded, error
            progress: 0,
            error: null,
        }));

        // Add to state
        setUploadedFiles((prev) => [...prev, ...newFiles]);

        const uploadIds = [];
        const errors = [];

        // Upload each file immediately
        for (const fileData of newFiles) {
            try {
                // Update status to uploading
                setUploadedFiles((prev) =>
                    prev.map((f) =>
                        f.id === fileData.id ? { ...f, status: 'uploading', progress: 10 } : f
                    )
                );

                // Create FormData for this file
                const formData = new FormData();
                formData.append('file', fileData.file);

                // Call temp upload endpoint
                // Backend's DeduplicationService handles hash checking and deduplication
                const response = await projectService.uploadFile(formData);
                const tempUploadId = response?.data?.id;

                if (tempUploadId) {
                    uploadIds.push(tempUploadId);

                    // Update status to uploaded
                    setUploadedFiles((prev) =>
                        prev.map((f) =>
                            f.id === fileData.id
                                ? { ...f, uploadId: tempUploadId, status: 'uploaded', progress: 100 }
                                : f
                        )
                    );
                } else {
                    throw new Error('No upload ID returned');
                }
            } catch (error) {
                console.error(`Failed to upload file ${fileData.name}:`, error);
                const errorMsg = error?.message || 'Upload failed';

                errors.push({
                    fileName: fileData.name,
                    message: errorMsg,
                });

                // Update status to error
                setUploadedFiles((prev) =>
                    prev.map((f) =>
                        f.id === fileData.id
                            ? { ...f, status: 'error', error: errorMsg }
                            : f
                    )
                );
            }
        }

        // Notify of any errors
        if (errors.length > 0) {
            const errorMessages = errors.map((e) => `${e.fileName}: ${e.message}`).join('\n');
            notify('error', `Some files failed to upload:\n${errorMessages}`);
        }

        return uploadIds;
    }, []);

    const removeFile = useCallback(async (fileId, uploadId) => {
        // If file still uploading, don't remove (operation in progress)
        const fileData = uploadedFiles.find(f => f.id === fileId);
        if (fileData?.status === 'uploading') {
            notify('error', 'Cannot remove file while uploading');
            return false;
        }

        // If file was uploaded (has uploadId), delete from backend
        if (uploadId && fileData?.status === 'uploaded') {
            try {
                await projectService.deleteTempUpload(uploadId);
                // Only remove from state if backend delete succeeds
                setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
                return true;
            } catch (error) {
                console.error(`Failed to delete temp upload ${uploadId}:`, error);
                notify('error', `Failed to remove file: ${error.message || 'Unknown error'}`);
                return false;
            }
        }

        // If file never uploaded, just remove from state
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
        return true;
    }, [uploadedFiles]);

    const clearAll = useCallback(() => {
        setUploadedFiles([]);
    }, []);

    const getUploadIds = useCallback(() => {
        return uploadedFiles
            .filter((f) => f.status === 'uploaded')
            .map((f) => f.uploadId);
    }, [uploadedFiles]);

    return {
        uploadedFiles,
        uploadFiles,
        removeFile,
        clearAll,
        getUploadIds,
    };
};
